/**
 * @file   src\Pages\EquipmentListing.tsx
 * @brief  Equipment Listing page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useNavigate, useEffect, useOutletContext, Container, Table, Badge, Button, Breadcrumb, Pagination, useParams, Modal, useLocation, Accordion } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import AddIcon from '../assets/images/icons/addicon';
import AddDeviceModal from '../components/AddDeviceModal';
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from '../utils/toastUtil';
import { AppContextType, BayDeviceDetail, equipmentArray } from '../interfaces/generalInterface';
import { EquipmentApi, CompanyApi, LocationApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import { ConnectivityType, DeviceType, EquipmentType } from '../utils/enums';
import QRCodeComponent from '../components/QRCodeComponent';
import SmallLoader from '../components/SmallLoader';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import CWSwitch from '../components/CWSwitch';
import { userType } from '../utils/enums';
import SyncIcon from '../assets/images/icons/syncicon';
import { formatDateTimeToUserTime, getFromLocalStorage } from '../utils/helper';
import TimeSettingsModal from '../components/TimeSettingsModal';

/** Fixed UI order: wash device (Arduino) always; PAX/LPR when API flags are 1 */
const BAY_DEVICE_TYPE_SLOTS: { deviceType: DeviceType; headerLabel: string }[] = [
  { deviceType: DeviceType.WASH_DEVICE, headerLabel: 'Arduino' },
  { deviceType: DeviceType.PAX, headerLabel: 'PAX' },
  { deviceType: DeviceType.LPR, headerLabel: 'LPR' },
];

function getBayDeviceTypeSlotsForRow(item: equipmentArray): { deviceType: DeviceType; headerLabel: string }[] {
  return BAY_DEVICE_TYPE_SLOTS.filter(({ deviceType }) => {
    if (deviceType === DeviceType.WASH_DEVICE) return true;
    if (deviceType === DeviceType.PAX) return item.IsPax === 1;
    if (deviceType === DeviceType.LPR) return item.IsLPR === 1;
    return false;
  });
}

function normalizeBayDeviceDetails(raw: equipmentArray['deviceDetails']): BayDeviceDetail[] {
  if (!raw || !Array.isArray(raw)) return [];
  return (raw as unknown[]).map((entry) => {
    const e = entry as Record<string, unknown>;
    return {
      DeviceID: String(e.DeviceID ?? e.deviceID ?? ''),
      DeviceName: String(e.DeviceName ?? e.deviceName ?? ''),
      DeviceType: Number(e.DeviceType ?? e.deviceType ?? 0),
      BayDeviceDetailID: Number(e.BayDeviceDetailID ?? e.bayDeviceDetailID ?? 0),
      HardwareStatus: e.HardwareStatus !== undefined ? Number(e.HardwareStatus) : e.hardwareStatus !== undefined ? Number(e.hardwareStatus) : undefined,
      StatusUpdateOn: e.StatusUpdateOn != null ? String(e.StatusUpdateOn) : e.statusUpdateOn != null ? String(e.statusUpdateOn) : undefined,
    };
  }).filter((d) => d.DeviceType > 0);
}

function getDeviceForType(details: BayDeviceDetail[], deviceType: DeviceType): BayDeviceDetail | undefined {
  return details.find((d) => d.DeviceType === deviceType);
}

function getWashDeviceHardwareFields(item: equipmentArray): { hardwareStatus: number; statusUpdateOn?: string; washDevice?: BayDeviceDetail } {
  const washDevice = getDeviceForType(normalizeBayDeviceDetails(item.deviceDetails), DeviceType.WASH_DEVICE);
  return {
    hardwareStatus: washDevice?.HardwareStatus ?? item.HardwareStatus,
    statusUpdateOn: washDevice?.StatusUpdateOn ?? item.statusUpdateOn,
    washDevice,
  };
}

const EquipmentListing = (props: any) => {
    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false

    const [loading, setLoading] = useState(false);
    const param = useParams();
    const location_id = param.id;
    const current_carwash_id = param.washid;
    const [current_locationName, setCurrent_locationName] = useState('');
    const [bayId, setBayId] = useState<number | null>(null)
    // To get path from which redirection was done
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const isLocationManager = userInfo?.userTypeId === 4 && userInfo?.permissionType === 2;
    const [isQueueEnabled, setIsQueueEnabled] = useState(false);

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const toaster = new MessageToaster();

    const navigate = useNavigate()
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    // To display heading
    useEffect(() => {
        handleUpdate()
    }, [current_locationName])

    // To get company details
    const getCompanyAddress = async () => {
        try {
            const param3 = `${current_carwash_id}`;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + param3) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany(data.data);
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Fetch company information on component mount
    useEffect(() => {
        getCompanyAddress();
    }, []);

    const [carwashGuid, setCarwashGuid] = useState<string | null>(null);
    const [subscriptionActive, setSubscriptionActive] = useState(0);
    // State to handle listed equipments
    const [equipments, setEquipments] = useState<equipmentArray[]>([]);
    const [currentPage, setCurrentPage] = useState(query.get('page') ? parseInt(query.get('page') as string, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    // Hardcoded for CARWASH-OWNER need to change useState({}) to useState([])
    const [companyDetails, setCompanyDetails] = useState<any>({});
    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param = `${current_carwash_id}`;

            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY + param) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCarwashGuid(data.data.carWashGUID);
            setSubscriptionActive(data.data.subscriptionActive);
            setCompanyDetails(data.data)
        } catch (error: any) {
            console.error('Error fetching location information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    // Function to fetch company information
    const getLocation = async () => {
        try {
            const param = `${location_id}`;
            const { data } = (!carwash_owner) ? await axios.get(LocationApi.GET_SINGLE_LOCATION + param) : await axios.get(LocationApi.CARWASH_OWNER_LOCATION_DETAILS + param);
            setCurrent_locationName(data.data.locationName);
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    const fetchQueueSetting = async () => {
        if (!location_id) return;
        try {
            const res = await axios.get(CompanyApi.CARWASH_OWNER_GET_QUEUE_SETTINGS + location_id);
            if (res?.data?.error === 0 && res.data?.data?.queueingEnabled !== undefined) {
                setIsQueueEnabled(res.data.data.queueingEnabled === true || res.data.data.queueingEnabled === 1);
            } else {
                toaster.toastError('Failed to load queue settings');
            }
        } catch (err) {
            toaster.toastError('Error fetching queue settings');
        }
    };

    const handleQueueSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setIsQueueEnabled(newValue);

        if (!location_id) {
            toaster.toastError("No location available to update queue setting");
            setIsQueueEnabled(!newValue);
            return;
        }

        try {
            setLoading(true);
            const payload = {
                locationId: Number(location_id),
                queueingEnabled: newValue,
            };

            const res = await axios.post(CompanyApi.CARWASH_OWNER_UPDATE_QUEUE_SETTINGS, payload);

            if (res?.data?.error === 0) {
                toaster.toastSuccess(res?.data?.message || "Queue setting updated");
            } else {
                toaster.toastError(res?.data?.message || "Failed to update setting");
                setIsQueueEnabled(!newValue);
            }
        } catch (err) {
            toaster.toastError("API error while updating queue setting");
            setIsQueueEnabled(!newValue);
        } finally {
            setLoading(false);
        }
    };

    // Fetch company information on component mount
    useEffect(() => {
        getCompany();
        getLocation();
        if (carwash_owner && !isLocationManager && location_id) {
            fetchQueueSetting();
        }
    }, []);


    const [selected_bay_for_delete, setSelected_bay_for_delete] = useState(0);
    const [selected_bay_name_for_delete, setSelected_bay_name_for_delete] = useState(0);
    const DeletebuttonClick = (delete_location_id: number, delete_bay_name: number) => {
        setSelected_bay_for_delete(delete_location_id);
        setSelected_bay_name_for_delete(delete_bay_name);
        setShowBayDeletepoup(true);
    }

    const [showBayDeletepoup, setShowBayDeletepoup] = useState<boolean>(false);
    //Hide ppopup
    const hideshowBayDeleteModal = () => {
        setShowBayDeletepoup(false);
    }

    const [showDeviceDetachpoup, setDeviceDetachpoup] = useState<boolean>(false);
    //Hide ppopup
    const hideDeviceDetachConfirmationModal = () => {
        setDeviceDetachpoup(false);
        setDeviceDetachBayId(0);
        setdeviceDetachBayName('');
        setDetachBayDeviceDetailId(0);
        setDeviceDetachDeviceType(0);
    }

    const [deviceDetachBayId, setDeviceDetachBayId] = useState<number>(0);
    const [deviceDetachBayName, setdeviceDetachBayName] = useState('');
    const [detachBayDeviceDetailId, setDetachBayDeviceDetailId] = useState(0);
    const [deviceDetachDeviceType, setDeviceDetachDeviceType] = useState(0);
    const Ondeletebay = async () => {
        try {
            setLoading(true); // Show loader
            const deleteBayRequest = {
                bayId: selected_bay_for_delete,
            };
            const responseBayDelete = (!carwash_owner) ?
                await axios.post(EquipmentApi.DELETE_EQUIPMENT, deleteBayRequest)
                : await axios.post(EquipmentApi.CARWASH_OWNER_DELETE_EQUIPMENT, deleteBayRequest)
            if (responseBayDelete?.data?.error === 0) {
                // window.location.reload();
                setShowBayDeletepoup(false);
            }
            else {
                toaster.toastError(responseBayDelete?.data?.message);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    const getEquipmentList = async (pageNo = 1) => {
        try {
            setLoading(true); // Show loader
            const param = (!carwash_owner) ? `${carwashGuid}/${location_id}?pageNo=${pageNo}` : `${location_id}?pageNo=${pageNo}`;
            const { data } = (!carwash_owner) ? await axios.get(EquipmentApi.EQUIPMENT_LIST + param) : await axios.get(EquipmentApi.CARWASH_OWNER_EQUIPMENT_LIST + param);

            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const equipment_list: equipmentArray[] = data.data.list;

                // Check if equipment_list is empty
                if (equipment_list.length > 0) {
                    // Transform the array to match the structure of the equipment interface
                    const transformedArray: equipmentArray[] = equipment_list;

                    setEquipments(transformedArray);
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('equipment list is empty');
                    setEquipments([]);  // Set to an empty array if equipment_list is empty
                }
            } else {
                console.log('equipment list is not available or is not an array');
                setEquipments([]);  // Set to an empty array if data is not available
            }
        } catch (error: any) {
            console.error('Error fetching equipment list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }

    };

    // Fetch equipment list when carwashGuid or currentPage changes
    useEffect(() => {
        if (carwashGuid) {
            getEquipmentList(currentPage);
        }
    }, [carwashGuid, currentPage, showBayDeletepoup]);

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };


    // Update the data
    const handleUpdate = () => {
        updateData({ heading: current_locationName, url: !carwash_owner ? `/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}` : `/${pageURLs.carwash_locations}`, showBackButton: true, showMainMenu: true, activeLink: !carwash_owner ? "manage-car-wash" : "manage-car-wash-locations" });
    };


    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [bayidAddDeviceModal, setBayidAddDeviceModal] = useState(0);
    const [addDeviceType, setAddDeviceType] = useState<number | undefined>(undefined);
    const handleCloseAddDeviceModal = () => {
        setShowAddDeviceModal(false);
        setAddDeviceType(undefined);
        if (carwashGuid) {
            getEquipmentList(currentPage);
        }
    }
    const handleShowAddDeviceModal = (id: number, deviceType?: number) => {
        if (id !== 0) {
            setBayidAddDeviceModal(id);
            setAddDeviceType(deviceType);
            setShowAddDeviceModal(true);
        }
    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    const formatLabel = (label: string) => {
        return label.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    const [showqrcodeModal, setShowqrcodeModal] = useState(false);
    const [qrcodeuniquekey, setQrcodeuniquekey] = useState(0);
    const [selectedbayname, setSelectedbayname] = useState(0);
    const Generateqr = (BayName: number, BayID: number) => {
        setQrcodeuniquekey(BayID);
        setSelectedbayname(BayName);
        setShowqrcodeModal(true);
    }
    const hideqrmodelModal = () => {
        setShowqrcodeModal(false);
    }
    //Synch individual bay's device status
    const syncBayDeviceStatus = async (bayId: number) => {
        try {
            setLoading(true); // Show loader
            const synchBayRequest = {
                bayId: bayId,
            };
            const responseBayDeviceStatusUpdate = (!carwash_owner) ?
                await axios.post(EquipmentApi.SYNC_BAY_EQUIPMENT_STATUS, synchBayRequest)
                : await axios.post(EquipmentApi.CARWASH_OWNER_SYNC_BAY_EQUIPMENT_STATUS, synchBayRequest);
            if (responseBayDeviceStatusUpdate?.data?.error === 0) {
                getEquipmentList(currentPage);
            }
            else {
                toaster.toastError(responseBayDeviceStatusUpdate?.data?.message);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    const showDeviceDetachConfirmPopup = (bayId: number, BayName: string, bayDeviceDetailId = 0, deviceTypeParam = 0) => {
        setdeviceDetachBayName(BayName);
        setDeviceDetachBayId(bayId);
        setDetachBayDeviceDetailId(bayDeviceDetailId);
        setDeviceDetachDeviceType(deviceTypeParam);
        setDeviceDetachpoup(true);
    }

    const removeDeviceFromBay = async (deviceTypeParam: number) => {
        try {
            setLoading(true); // Show loader

            const detachPayload: Record<string, unknown> = {
                deviceId: 0,
                bayId: deviceDetachBayId,
                locationId: location_id,
                deviceType: deviceTypeParam,
            };
            if (detachBayDeviceDetailId > 0) {
                detachPayload.bayDeviceDetailId = detachBayDeviceDetailId;
            }
            const responseLocationEquipmentUpdate = (!carwash_owner) ?
                await axios.post(EquipmentApi.UPDATE_EQUIPMENT_DEVICE_ID, detachPayload)
                :
                await axios.post(EquipmentApi.CARWASH_OWNER_UPDATE_EQUIPMENT_DEVICE_ID, detachPayload)
                ;
            if (responseLocationEquipmentUpdate?.data?.error === 0) {
                hideDeviceDetachConfirmationModal()
                getEquipmentList(currentPage);
            }
            else {
                toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    const [showTimeSettingsModal, setShowTimeSettingsModal] = useState(false);
    const handleCloseTimeSettingsModal = () => setShowTimeSettingsModal(false);
    const handleShowTimeSettingsModal = (bayId: number) => {
        setBayId(bayId)
        setShowTimeSettingsModal(true);
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {
                    (carwash_owner === true)
                    &&
                    <div className="manage-header-block d-flex justify-content-end">
                        <Button
                            title='Add Bay'
                            type="submit"
                            variant="primary"
                            className="primary-btn ms-auto d-flex align-items-center"
                            onClick={() => navigate(`/${pageURLs.add_equipment}/${location_id}`, { state: { current_location_id: location_id } })}

                        >
                            Add New Bay
                            <AddIcon />
                        </Button>

                    </div>
                }
                <Breadcrumb>
                    {!carwash_owner ? (
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}`) }}>{company?.companyName}</Breadcrumb.Item>
                            <Breadcrumb.Item active>{current_locationName}</Breadcrumb.Item>
                        </>
                    ) : (
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/${pageURLs.carwash_locations}`) }}>Manage Car Wash Locations</Breadcrumb.Item>
                            <Breadcrumb.Item active>{current_locationName}</Breadcrumb.Item>

                        </>
                    )}
                </Breadcrumb>
                <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                    {companyDetails && <GenerateLink id={current_carwash_id} companyDetails={companyDetails} userType={props.userType} carwash_owner={carwash_owner} />}
                    <div className="activity-status d-flex">
                        {
                            (!carwash_owner)
                                ?
                                <>
                                    <CarWashStatus carWashId={current_carwash_id} status={(companyDetails?.IsCompanyActive == 1) ? true : false} companyName={company?.companyName} />

                                    <div className="stat-group d-flex align-items-center">
                                        <p>Subscription Status</p>
                                        <Badge className={subscriptionActive === 1 ? 'active' : 'inactive'}>
                                            {subscriptionActive === 1 ? 'Active' : (subscriptionActive === 0) ? 'Inactive' : 'N/A'}
                                        </Badge>
                                    </div>
                                </>
                                :
                                <div className="stat-group d-flex align-items-center">
                                    <p>Subscription Status</p>
                                    <Badge className={subscriptionActive === 1 ? 'active' : 'inactive'}>
                                        {subscriptionActive === 1 ? 'Active' : (subscriptionActive === 0) ? 'Inactive' : 'N/A'}
                                    </Badge>
                                </div>
                        }


                    </div>
                </div>

                {carwash_owner && !isLocationManager && location_id ? (
                    <div className="car-info-sec d-flex flex-column align-items-end mb-3">
                        <div className="info-switch d-flex align-items-center">
                            <h5 className="f-bold">Is Queue Enabled</h5>
                            <CWSwitch
                                id="queueswitch"
                                name="queueswitch"
                                checked={isQueueEnabled}
                                onChange={handleQueueSwitchChange}
                            />
                        </div>
                        <div className="desc">
                            <span className="fw-bold">Note:</span> Enable this to allow queue functionality for this location
                        </div>
                    </div>
                ) : null}

                {
                    (!carwash_owner)
                    &&
                    <>
                        <div className="manage-header-block d-flex justify-content-end">
                            <Button
                                title='Add Bay'
                                type="submit"
                                variant="primary"
                                className="primary-btn ms-auto d-flex align-items-center"
                                onClick={() => navigate(`/superadmin/${pageURLs.add_equipment}/${location_id}/${current_carwash_id}`, { state: { current_carwash_id: current_carwash_id, current_location_id: location_id } })}

                            >
                                Add Bay
                                <AddIcon />
                            </Button>
                        </div>
                        <div className="table-title-block d-flex align-items-center justify-content-between">
                            <h5 className='f-bold'>{current_locationName}</h5>
                        </div>
                    </>
                }
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Bay Type</th>
                                <th>Bay Number/Bay Name</th>
                                <th>Brand of Pay Station</th>
                                <th>Internet Connectivity Type</th>

                                <th>Hardware Status</th>
                                <th>QR Code</th>
                                <th>Sign Generation</th>
                                <th></th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {equipments && equipments.length > 0 ? (
                                equipments.map((item) => {
                                    const { hardwareStatus, statusUpdateOn, washDevice } = getWashDeviceHardwareFields(item);
                                    return (
                                    <tr key={item.BayID}>
                                        <td>
                                            {formatLabel(
                                                Object.keys(EquipmentType).find(
                                                    key => EquipmentType[key as keyof typeof EquipmentType] === item.BayTypeID
                                                ) || 'Unknown Bay Type'
                                            )}
                                            <br />
                                            {/* {item.deviceID ? <><p><span className='title'>Device:</span> {item.DeviceName}</p>
                                                <Button title="Remove Device" variant="outline-primary remove-btn" className="outline-btn" onClick={() => showDeviceDetachConfirmPopup(item.BayID, item.BayName)}>Remove Device</Button></>
                                                : <Button title="Add Device" variant="outline-primary add-btn" className="outline-btn" onClick={() => handleShowAddDeviceModal(item.BayID)}>Add Device</Button>
                                            } */}

                                            <Accordion className="device-list">
                                                {getBayDeviceTypeSlotsForRow(item).map(({ deviceType, headerLabel }) => {
                                                    const bayDetails = normalizeBayDeviceDetails(item.deviceDetails);
                                                    const slotDevice = getDeviceForType(bayDetails, deviceType);
                                                    return (
                                                        <Accordion.Item eventKey={String(deviceType)} key={deviceType}>
                                                            <Accordion.Header>{headerLabel}</Accordion.Header>
                                                            <Accordion.Body>
                                                                {slotDevice ? (
                                                                    <>
                                                                        <div className="name"><span>Device:</span> {slotDevice.DeviceName}</div>
                                                                        <Button
                                                                            title="Remove Device"
                                                                            variant="outline-primary remove-btn mx-auto d-flex"
                                                                            className="outline-btn"
                                                                            onClick={() => showDeviceDetachConfirmPopup(
                                                                                item.BayID,
                                                                                String(item?.BayName ?? item?.BayNumber),
                                                                                slotDevice.BayDeviceDetailID,
                                                                                deviceType,
                                                                            )}
                                                                        >
                                                                            Remove Device
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="name"><span>No Device Added</span></div>
                                                                        <Button
                                                                            title="Add Device"
                                                                            variant="outline-primary add-btn mx-auto d-flex"
                                                                            className="outline-btn"
                                                                            onClick={() => handleShowAddDeviceModal(item.BayID, deviceType)}
                                                                        >
                                                                            Add Device
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    );
                                                })}
                                            </Accordion>
                                        </td>
                                        <td>{item?.BayName ? item?.BayName : item?.BayNumber}
                                        </td>

                                        <td>{item.PayStationBrand}</td>
                                        <td>
                                            {formatLabel(
                                                Object.keys(ConnectivityType).find(
                                                    key => ConnectivityType[key as keyof typeof ConnectivityType] === item.InternetTypeID
                                                ) || 'Unknown Connectivity'
                                            )}
                                        </td>

                                        <td>
                                            <Badge className={hardwareStatus === 1 ? 'active' : 'inactive'}>{hardwareStatus === 1 ? 'Active' : 'Inactive'}</Badge>
                                            {washDevice && <div className='sync-sec mt-2'>
                                                {statusUpdateOn && <p>Last updated on {formatDateTimeToUserTime(statusUpdateOn)}</p>}
                                                <Button variant="secondary" className="d-flex align-items-center secondary-btn btn-details" onClick={() => syncBayDeviceStatus(item.BayID)}>
                                                    Sync now <SyncIcon />
                                                </Button>
                                            </div>}
                                        </td>
                                        <td className='nowrap'><Button title="View QR Code" variant="outline-primary" className="outline-btn" onClick={() => { Generateqr(item.BayNumber, item.BayID) }} disabled={item.DeviceID === ""}>View QR Code</Button></td>
                                        <td className='nowrap'>
                                            <Button
                                                title="View Templates"
                                                variant="outline-primary"
                                                className="outline-btn"
                                                onClick={() => {
                                                    if (props.userType !== userType['CARWASH-OWNER']) {
                                                        if(item.BayTypeID!=EquipmentType['In Bay'] && (item.BayTypeID!=EquipmentType['Tunnel']))
                                                        {
                                                            navigate(`/superadmin/${pageURLs.carwash_small_sign_generation}/${item.BayID}`);
                                                        }
                                                        else{
                                                             navigate(`/superadmin/${pageURLs.carwash_sign_generation}/${item.BayID}`);
                                                        }
                                                    } else {
                                                        if(item.BayTypeID!=EquipmentType['In Bay'] && (item.BayTypeID!=EquipmentType['Tunnel']))
                                                        {
                                                            navigate(`/${pageURLs.carwash_small_sign_generation}/${item.BayID}`);
                                                        }
                                                        else{
                                                            navigate(`/${pageURLs.carwash_sign_generation}/${item.BayID}`);
                                                        }
                                                    }
                                                }}
                                            >
                                                View Templates
                                            </Button>
                                        </td>
                                        <td>
                                            {item.BayTypeID === 1 ? <Button
                                                type="submit"
                                                variant="outline-primary"
                                                className='outline-btn'
                                                // className="primary-btn ms-3"
                                                onClick={() => handleShowTimeSettingsModal(item.BayID)}

                                            >
                                                Set Hours
                                            </Button> : null}
                                        </td>
                                        <td className='nowrap'>
                                            <div className="active-group d-flex align-items-center">

                                                {/* <Button title="Download" variant="light" className="light-btn action-btn" onClick={() => { alert("Not Implemented.") }}><DownloadIcon /></Button> */}
                                                {
                                                    props.userType !== userType['CARWASH-OWNER'] ? (
                                                        <Button title="Edit" variant="light" className="light-btn action-btn" onClick={() => { navigate(`/superadmin/${pageURLs.edit_equipment}/${location_id}/${current_carwash_id}/${item.BayID}`, { state: { page: currentPage, current_carwash_id: current_carwash_id, current_location_id: location_id } }) }}><EditIcon /></Button>
                                                    ) : (
                                                        <Button title="Edit" variant="light" className="light-btn action-btn" onClick={() => { navigate(`/${pageURLs.edit_equipment}/${location_id}/${item.BayID}`, { state: { page: currentPage, current_carwash_id: current_carwash_id, current_location_id: location_id } }) }}><EditIcon /></Button>
                                                    )
                                                }

                                                <Button title="Delete" variant="light" className="light-btn action-btn" onClick={() => DeletebuttonClick(item.BayID, item.BayNumber)}><DeleteIcon /></Button>

                                            </div>

                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                </tr>
                            )}


                        </tbody>
                    </Table>
                    <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div>
                </div>

            </Container>

            <AddDeviceModal
                show={showAddDeviceModal}
                onHide={handleCloseAddDeviceModal}
                bayID={bayidAddDeviceModal}
                location_id={location_id}
                cuserType={props.userType}
                carwash_id={current_carwash_id}
                deviceType={addDeviceType}
            />
            <Modal
                show={showqrcodeModal}
                onHide={hideqrmodelModal}
                centered
                className='add-modal'
            >
                <Modal.Header closeButton></Modal.Header>
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>QR Code of Bay {selectedbayname}</h5>
                    <div className="btn-sec">
                        <QRCodeComponent value={qrcodeuniquekey.toString()} equipment_name={selectedbayname?.toString()} />
                        {/* <Button variant="light" className="secondary-btn" onClick={hideqrmodelModal}>Close</Button> */}
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={showBayDeletepoup}
                onHide={hideshowBayDeleteModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Do you want to delete the Bay  {selected_bay_name_for_delete}?</h5>
                    <div className="btn-sec d-grid">
                        <Button variant="light" className="secondary-btn" onClick={hideshowBayDeleteModal}>Not Now</Button>
                        <Button variant="primary" className='primary-btn btn-main m-0' onClick={Ondeletebay}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={showDeviceDetachpoup}
                onHide={hideDeviceDetachConfirmationModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Do you want to remove this device from the bay {deviceDetachBayName}?</h5>
                    <div className="btn-sec d-grid">
                        <Button variant="light" className="secondary-btn" onClick={hideDeviceDetachConfirmationModal}>Not Now</Button>
                        <Button variant="primary" className='primary-btn btn-main m-0' onClick={() => removeDeviceFromBay(deviceDetachDeviceType)}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>
            <TimeSettingsModal
                show={showTimeSettingsModal}
                onHide={handleCloseTimeSettingsModal}
                bayId={bayId}
                carwash_owner={carwash_owner}
            />
            {loading && <SmallLoader />}
        </div>
    )
}

export default EquipmentListing;
